/* eslint-disable */
import * as React from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { ChevronRight, ChevronLeft } from 'lucide-react'

import { cn } from '../../lib/utils'
import { Button } from './button'

const CarouselContext = React.createContext(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

const Carousel = React.forwardRef(
  (
    { orientation = 'horizontal', opts, setApi, plugins, autoHeight = true, className, children, ...props },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)
    const [containerHeight, setContainerHeight] = React.useState(null)

    const onSelect = React.useCallback(
      (api) => {
        if (!api) {
          return
        }

        setCanScrollPrev(api.canScrollPrev())
        setCanScrollNext(api.canScrollNext())

        // Auto-adjust height to match the currently selected slide (if enabled)
        if (!autoHeight) return

        try {
          const selectedIndex = api.selectedScrollSnap()
          const slideNodes = api.slideNodes?.()
          const selectedSlide = slideNodes?.[selectedIndex]
          if (selectedSlide) {
            const newHeight = selectedSlide.offsetHeight
            if (newHeight && Number.isFinite(newHeight)) {
              setContainerHeight(newHeight)
            }
          }
        } catch (e) {
          // Fail silently if measurement isn't available
        }
      },
      [autoHeight],
    )

    // const scrollPrev = React.useCallback(() => {
    //   api?.scrollPrev()
    // }, [api])

    // const scrollNext = React.useCallback(() => {
    //   api?.scrollNext()
    // }, [api])

    const scrollPrev = React.useCallback(() => {
      if (api) {
        const currentIndex = api.selectedScrollSnap()
        const targetIndex = Math.max(currentIndex - 3, 0)
        api.scrollTo(targetIndex)
      }
    }, [api])

    const scrollNext = React.useCallback(() => {
      if (api) {
        const currentIndex = api.selectedScrollSnap()
        const targetIndex = Math.min(currentIndex + 3, api.scrollSnapList().length - 1)
        api.scrollTo(targetIndex)
      }
    }, [api])

    const handleKeyDown = React.useCallback(
      (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext],
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      return () => {
        api?.off('select', onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
          containerHeight,
          autoHeight,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role='region'
          aria-roledescription='carousel'
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  },
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef(({ className, ...props }, ref) => {
  const { carouselRef, orientation, containerHeight, autoHeight } = useCarousel()

  return (
    <div
      ref={carouselRef}
      className='overflow-hidden'
      style={
        autoHeight && containerHeight
          ? {
            height: containerHeight,
            transition: 'height 0.3s ease',
          }
          : undefined
      }
    >
      <div
        ref={ref}
        className={cn(
          'flex items-start',
          orientation === 'horizontal' ? 'ml-0' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef(({ className, ...props }, ref) => {
  const { orientation } = useCarousel()

  return (
    <div
      ref={ref}
      role='group'
      aria-roledescription='slide'
      className={cn(
        'w-full shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-0 ml-0' : 'pt-4',
        className,
      )}
      {...props}
    />
  )
})
CarouselItem.displayName = 'CarouselItem'

const CarouselPrevious = React.forwardRef(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel()

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          `absolute hidden justify-center items-center h-8 w-8 rounded-full border-none bg-[#ECEEF4] ${!canScrollPrev ? 'md:hidden' : 'md:flex'
          }`,
          orientation === 'horizontal'
            ? '-left-3 top-1/2 -translate-y-1/2'
            : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
          className,
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        {...props}
      >
        <ChevronLeft className='h-4 w-4 text-primary-500' />
        <span className='sr-only'>Previous slide</span>
      </Button>
    )
  },
)
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel()

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          `absolute hidden  justify-center items-center h-8 w-8 rounded-full border-none bg-[#ECEEF4] ${!canScrollNext ? 'md:hidden' : 'md:flex'
          }`,
          orientation === 'horizontal'
            ? '-right-3 top-1/2 -translate-y-1/2'
            : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
          className,
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        <ChevronRight className='h-4 w-4 text-primary-500' />
        <span className='sr-only'>Next slide</span>
      </Button>
    )
  },
)
CarouselNext.displayName = 'CarouselNext'

export { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext }
